import { createRemoteJWKSet, jwtVerify, type JWTPayload } from 'jose'
import type { FastifyBaseLogger } from 'fastify'
import { createLogger } from '@/common/utils/logger'
import { isValidUserRole, isString, isNonEmptyString } from '../../common/utils/type-guards.js'

const DEFAULT_ROLES_CLAIM = 'https://roadtripper.io/roles'
const PLAN_CLAIMS = [
  'https://roadtripper.io/plan',
  'https://roadtripper.app/plan',
  'https://roadtripper.dev/plan',
  'plan'
]
const SUBSCRIPTION_CLAIMS = [
  'https://roadtripper.io/subscription',
  'subscriptionStatus'
]

let jwks: ReturnType<typeof createRemoteJWKSet> | null = null

const auth0Domain = process.env.AUTH0_DOMAIN
const auth0Audience = process.env.AUTH0_AUDIENCE
const rolesClaim = process.env.AUTH0_ROLES_CLAIM || DEFAULT_ROLES_CLAIM
const env = (process.env.NODE_ENV || 'development').toLowerCase()
const isProdEnv = env === 'production' || env === 'prod'
// ⚠️ E2E bypass allowed in: explicit E2E mode OR non-production environments
const e2eAuthBypass =
  process.env.E2E_AUTH_BYPASS === 'true' ||
  process.env.MODE === 'e2e' ||
  !isProdEnv
const e2eAuthToken = process.env.E2E_AUTH_TOKEN || 'e2e-test-token'
const e2eAuthUserRaw = process.env.E2E_AUTH_USER
const e2eLogger = createLogger('Auth0E2E')

function ensureIssuer(): string {
  if (!auth0Domain) {
    throw new Error('AUTH0_DOMAIN is not configured')
  }

  if (auth0Domain.startsWith('http://') || auth0Domain.startsWith('https://')) {
    return auth0Domain.replace(/\/$/, '')
  }

  return `https://${auth0Domain.replace(/\/$/, '')}`
}

function ensureAudience(): string | undefined {
  return isNonEmptyString(auth0Audience) ? auth0Audience : undefined
}

function getJwks(): ReturnType<typeof createRemoteJWKSet> {
  if (!jwks) {
    const issuer = ensureIssuer()
    const jwksUrl = new URL(`${issuer}/.well-known/jwks.json`)
    jwks = createRemoteJWKSet(jwksUrl)
  }
  return jwks
}

function toArray(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  const result: string[] = []
  for (const item of value) {
    const strValue = String(item)
    if (strValue.length > 0) {
      result.push(strValue)
    }
  }
  return result
}

function extractRoles(payload: JWTPayload): string[] {
  const roles = new Set<string>()
  const explicitClaim = payload[rolesClaim]
  if (explicitClaim) {
    for (const role of toArray(explicitClaim)) {
      roles.add(role)
    }
  }

  // Common alternative claim keys
  const fallbacks = ['roles', 'https://hasura.io/jwt/claims']
  for (const key of fallbacks) {
    const value = payload[key]
    if (Array.isArray(value)) {
      for (const role of value) {
        roles.add(String(role))
      }
    } else if (key === 'https://hasura.io/jwt/claims' && value && typeof value === 'object') {
      const hasuraRoles = (value as Record<string, unknown>)['x-hasura-roles']
      for (const role of toArray(hasuraRoles)) {
        roles.add(role)
      }
    }
  }

  return Array.from(roles)
}

function extractPlan(payload: JWTPayload): string | undefined {
  for (const key of PLAN_CLAIMS) {
    const value = payload[key]
    if (isNonEmptyString(value)) {
      return value.trim()
    }
  }
  if (payload.app_metadata && typeof payload.app_metadata === 'object') {
    const metaPlan = (payload.app_metadata as Record<string, unknown>).plan
    if (isNonEmptyString(metaPlan)) {
      return metaPlan.trim()
    }
  }
  return undefined
}

function extractSubscriptionStatus(payload: JWTPayload): string | undefined {
  for (const key of SUBSCRIPTION_CLAIMS) {
    const value = payload[key]
    if (isNonEmptyString(value)) {
      return value.trim()
    }
  }
  return undefined
}

export interface AuthUser {
  uid: string
  sub: string
  email?: string
  emailVerified?: boolean
  name?: string
  nickname?: string
  picture?: string
  roles: string[]
  permissions: string[]
  plan?: string
  premium?: boolean
  admin?: boolean
  role: 'user' | 'moderator' | 'admin' | 'superadmin'
  subscriptionStatus?: string
  claims: JWTPayload
}



function buildAuthUser(payload: JWTPayload): AuthUser {
  const uid = String(payload.sub ?? '')
  if (!uid) {
    throw new Error('Auth0 token missing subject (sub) claim')
  }

  const roles = extractRoles(payload)
  const permissions = toArray(payload.permissions)
  const plan = extractPlan(payload)
  const subscriptionStatus = extractSubscriptionStatus(payload)
  const hasSuperAdmin = roles.includes('superadmin')
  const hasAdmin = roles.includes('admin') || hasSuperAdmin
  const hasModerator = roles.includes('moderator')
  const admin = hasAdmin
  const premium = roles.includes('premium') || plan === 'premium'

  let role: AuthUser['role'] = 'user'
  if (hasSuperAdmin) {
    role = 'superadmin'
  } else if (hasAdmin) {
    role = 'admin'
  } else if (hasModerator) {
    role = 'moderator'
  }

  return {
    uid,
    sub: uid,
    email: isString(payload.email) ? payload.email : undefined,
    emailVerified: typeof payload.email_verified === 'boolean' ? payload.email_verified : undefined,
    name: isString(payload.name) ? payload.name : undefined,
    nickname: isString(payload.nickname) ? payload.nickname : undefined,
    picture: isString(payload.picture) ? payload.picture : undefined,
    roles,
    permissions,
    plan,
    premium,
    admin,
    role,
    subscriptionStatus,
    claims: payload
  }
}

function buildE2EMockUser(): AuthUser {
  const base: AuthUser = {
    uid: 'codex-e2e',
    sub: 'codex-e2e',
    email: 'codex-e2e@roadtrip.test',
    emailVerified: true,
    name: 'Codex Runner',
    nickname: 'codex',
    picture: undefined,
    roles: ['admin', 'user'],
    permissions: [],
    plan: 'premium',
    premium: true,
    admin: true,
    role: 'admin',
    subscriptionStatus: 'active',
    claims: {}
  }

  if (typeof e2eAuthUserRaw === 'string' && e2eAuthUserRaw.trim().length > 0) {
    try {
      const parsed = JSON.parse(e2eAuthUserRaw) as Record<string, unknown>
      const parsedRoles = Array.isArray(parsed.roles)
        ? parsed.roles
          .map(role => (isString(role) ? role.trim() : ''))
          .filter((role): role is string => role.length > 0)
        : base.roles
      const parsedPermissions = Array.isArray(parsed.permissions)
        ? parsed.permissions
          .map(permission => (isString(permission) ? permission.trim() : ''))
          .filter((permission): permission is string => permission.length > 0)
        : base.permissions

      const merged: AuthUser = {
        ...base,
        ...parsed,
        email: isString(parsed.email) ? parsed.email : base.email,
        name: isString(parsed.name) ? parsed.name : base.name,
        nickname: isString(parsed.nickname) ? parsed.nickname : base.nickname,
        picture: isString(parsed.picture) ? parsed.picture : undefined,
        plan: isString(parsed.plan) ? parsed.plan : base.plan,
        subscriptionStatus:
          isString(parsed.subscriptionStatus) ? parsed.subscriptionStatus : base.subscriptionStatus,
        admin: typeof parsed.admin === 'boolean' ? parsed.admin : base.admin,
        premium: typeof parsed.premium === 'boolean' ? parsed.premium : base.premium,
        role: isValidUserRole(parsed.role) ? parsed.role : base.role,
        roles: parsedRoles,
        permissions: parsedPermissions
      }

      return merged
    } catch (error) {
      // Keep base mock user if parsing fails
      e2eLogger.warn('[Auth] Failed to parse E2E_AUTH_USER, using default mock user', { error })
      return base
    }
  }

  return base
}

export async function verifyAuth0Token(token: string, logger?: FastifyBaseLogger): Promise<AuthUser> {
  if (!isNonEmptyString(token)) {
    throw new Error('Missing or empty authorization token')
  }

  // 🎯 E2E bypass: accept non-JWT tokens in non-prod when allowed
  const bypassAllowed = e2eAuthBypass && !['production', 'prod'].includes(env)
  const tokenParts = token.split('.')
  const looksLikeJwt = tokenParts.length === 3

  // 🔍 DEBUG: Log token structure (first 50 chars only for security)
  logger?.debug({
    tokenPrefix: token.substring(0, 50),
    tokenParts: tokenParts.length,
    looksLikeJwt,
    bypassAllowed,
    env
  }, '[Auth] Token validation check')

  if (bypassAllowed) {
    if (!looksLikeJwt) {
      logger?.info('[Auth] Non-JWT token accepted via E2E bypass')
      return buildE2EMockUser()
    }
    if (token === e2eAuthToken) {
      logger?.info('[Auth] E2E token matched, using mock user')
      return buildE2EMockUser()
    }
  }

  // ⚠️ SECURITY: Validate token format (JWT has 3 parts separated by dots)
  if (tokenParts.length !== 3) {
    logger?.error({
      tokenPrefix: token.substring(0, 50),
      tokenParts: tokenParts.length
    }, '[Auth] Invalid token format - not a JWT')
    throw new Error('Invalid token format')
  }

  try {
    const issuer = ensureIssuer()
    const audience = ensureAudience()

    // Auth0 tokens include trailing slash in iss claim, so we need to accept both formats
    const issuerWithSlash = issuer.endsWith('/') ? issuer : `${issuer}/`

    const { payload } = await jwtVerify(token, getJwks(), {
      issuer: issuerWithSlash,
      audience
    })

    return buildAuthUser(payload)
  } catch (error) {
    // ⚠️ SECURITY: Log error for debugging but don't expose details to client
    logger?.warn({ err: error }, '[Auth] Token verification failed')
    throw new Error('Invalid or expired token')
  }
}
